import { Process } from "../api/recipeSimulator";
import {
  ProcessDirectionOption,
  RecipeDataTypesOption,
  TemperatureUnitOptions
} from "../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../config/enums/MeasurementTypes";
import { RecipeTypeClassification } from "../config/enums/recipeSimulator.enum";
import { pickNextColor } from "../utils/recipeColor.utill";

const EMPTY_NUMERIC_VALUES = new Set<unknown>([null, undefined, "", "null"]);

export const toApiNumber = (value: unknown, fallback = 0): number => {
  if (EMPTY_NUMERIC_VALUES.has(value)) {
    return fallback;
  }

  const num = Number(value);
  return Number.isNaN(num) ? fallback : num;
};

export const toNullableApiNumber = (value: unknown): number | null => {
  if (EMPTY_NUMERIC_VALUES.has(value)) {
    return null;
  }

  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

export const getValidElectricityUnit = (unit?: number): number => {
  return unit === MEASUREMENT_TYPES.POWER_kWh_KG || unit === MEASUREMENT_TYPES.POWER_kWh_TON
    ? unit
    : MEASUREMENT_TYPES.POWER_kWh_KG;
};

export const getValidWastewaterUnit = (unit?: number): number => {
  return unit === MEASUREMENT_TYPES.VOLUME_m3_PER_KG || unit === MEASUREMENT_TYPES.VOLUME_m3_PER_TON
    ? unit
    : MEASUREMENT_TYPES.VOLUME_m3_PER_KG;
};

export const getValidHeatUnit = (unit?: number): number => {
  return unit === MEASUREMENT_TYPES.ENERGY_MJ_KG || unit === MEASUREMENT_TYPES.ENERGY_MJ_TON
    ? unit
    : MEASUREMENT_TYPES.ENERGY_MJ_KG;
};

export interface BuildProcessPayloadOptions {
  defaultTemperatureUnit?: string;
  variantId?: string | null;
  usedColors?: string[];
  fallbackGenericProcessId?: string | number | null;
  preserveRelationshipFrom?: Pick<
    Process,
    "originalRecipeId" | "originalProcessId" | "recipeTypeClassification"
  >;
  overrides?: Partial<Process>;
}

export const buildRecipeProcessPayload = (
  data: Record<string, any>,
  options: BuildProcessPayloadOptions = {}
): Process => {
  const {
    defaultTemperatureUnit = TemperatureUnitOptions.CELSIUS,
    variantId,
    usedColors = [],
    fallbackGenericProcessId,
    preserveRelationshipFrom,
    overrides = {}
  } = options;

  const isSemiSpecific = data.dataType === RecipeDataTypesOption.SEMI_SPECIFIC;
  const { isCollapsed, saveStatue, isNewProcess, isUpdated, ...processData } = data;

  const payload: Process = {
    ...processData,
    processName: processData.processName?.trim() || "",
    input: toApiNumber(processData.input),
    output: toApiNumber(processData.output),
    country: toApiNumber(processData.country),
    sideStream: toApiNumber(processData.sideStream),
    electricity: toApiNumber(processData.electricity),
    heat: toApiNumber(processData.heat),
    wastewater: toApiNumber(processData.wastewater),
    inputUnit: processData.inputUnit || MEASUREMENT_TYPES.WEIGHT_KG,
    outputUnit: processData.outputUnit || MEASUREMENT_TYPES.WEIGHT_KG,
    sideStreamUnit: processData.sideStreamUnit || MEASUREMENT_TYPES.WEIGHT_KG,
    electricityUnit: getValidElectricityUnit(processData.electricityUnit),
    wastewaterUnit: getValidWastewaterUnit(processData.wastewaterUnit),
    heatUnit: getValidHeatUnit(processData.heatUnit),
    genericProcessId: toNullableApiNumber(processData.genericProcessId ?? fallbackGenericProcessId),
    water: toApiNumber(processData.water),
    carbohydrates: toApiNumber(processData.carbohydrates),
    protein: toApiNumber(processData.protein),
    fat: toApiNumber(processData.fat),
    ashMinerals: toApiNumber(processData.ashMinerals),
    starch: toApiNumber(processData.starch),
    hydrocolloidsGums: toApiNumber(processData.hydrocolloidsGums),
    fiber: toApiNumber(processData.fiber),
    gellingProtein: toApiNumber(processData.gellingProtein),
    gelatin: toApiNumber(processData.gelatin),
    solidFat: toApiNumber(processData.solidFat),
    startingTemperature: toApiNumber(processData.startingTemperature),
    targetTemperature: toApiNumber(processData.targetTemperature),
    maintenanceTime: toApiNumber(processData.maintenanceTime),
    targetTemperatureUnit: processData.targetTemperatureUnit || defaultTemperatureUnit,
    startingTemperatureUnit: processData.startingTemperatureUnit || defaultTemperatureUnit,
    coolingMethod: isSemiSpecific ? toNullableApiNumber(processData.coolingMethod) : undefined,
    durationValue: isSemiSpecific ? toApiNumber(processData.durationValue) : undefined,
    durationType: isSemiSpecific ? processData.durationType || "HOURS" : undefined,
    extendedStorage: isSemiSpecific ? !!processData.extendedStorage : undefined,
    processDirection: isSemiSpecific ? processData.processDirection : undefined,
    chartColor: processData.chartColor || pickNextColor(usedColors),
    ...overrides
  };

  if (preserveRelationshipFrom) {
    payload.originalRecipeId = preserveRelationshipFrom.originalRecipeId;
    payload.originalProcessId = preserveRelationshipFrom.originalProcessId;
    payload.recipeTypeClassification = preserveRelationshipFrom.recipeTypeClassification;
  } else if (variantId) {
    payload.originalRecipeId = processData.originalRecipeId || "";
    payload.originalProcessId = processData.originalProcessId || "";
    payload.recipeTypeClassification =
      processData.recipeTypeClassification || RecipeTypeClassification.PRIVATE;
  }

  return payload;
};

type NormalizedFormFieldKeys =
  | "country"
  | "genericProcessId"
  | "coolingMethod"
  | "targetTemperatureUnit"
  | "startingTemperatureUnit"
  | "electricityUnit"
  | "wastewaterUnit"
  | "heatUnit"
  | "processDirection";

type NormalizedFormFields = {
  country?: string;
  genericProcessId?: string | null;
  coolingMethod?: string | null;
  targetTemperatureUnit?: string;
  startingTemperatureUnit?: string;
  electricityUnit?: number;
  wastewaterUnit?: number;
  heatUnit?: number;
  processDirection?: string;
};

export type NormalizedProcessFormValues<T extends Record<string, any>> = Omit<
  T,
  NormalizedFormFieldKeys
> &
  NormalizedFormFields;

/** Canonical form-layer process shape derived from API Process. */
export type ProcessFormValues = NormalizedProcessFormValues<Process>;

export interface NormalizeProcessFormOptions {
  defaultCountry?: string;
  defaultTemperatureUnit?: string;
}

export function normalizeProcessForForm(
  process: Process,
  options?: NormalizeProcessFormOptions
): ProcessFormValues;
export function normalizeProcessForForm<T extends Record<string, any>>(
  process: T,
  options?: NormalizeProcessFormOptions
): NormalizedProcessFormValues<T>;
export function normalizeProcessForForm<T extends Record<string, any>>(
  process: T,
  options: NormalizeProcessFormOptions = {}
): NormalizedProcessFormValues<T> {
  const { defaultCountry = "47", defaultTemperatureUnit = TemperatureUnitOptions.CELSIUS } =
    options;

  return {
    ...process,
    country: process.country != null ? String(process.country) : defaultCountry,
    genericProcessId:
      toNullableApiNumber(process.genericProcessId) != null
        ? String(process.genericProcessId)
        : null,
    coolingMethod:
      toNullableApiNumber(process.coolingMethod) != null ? String(process.coolingMethod) : null,
    targetTemperatureUnit: process.targetTemperatureUnit || defaultTemperatureUnit,
    startingTemperatureUnit: process.startingTemperatureUnit || defaultTemperatureUnit,
    electricityUnit: getValidElectricityUnit(process.electricityUnit),
    wastewaterUnit: getValidWastewaterUnit(process.wastewaterUnit),
    heatUnit: getValidHeatUnit(process.heatUnit),
    processDirection: process.processDirection || ProcessDirectionOption.HEATING
  };
}
